/**
 * /policies/travel/booking-class-rules — Session 3c PR 24.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { BookingClassRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function TravelBookingClassPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-travel-booking-class", le],
    queryFn: () => session3cApi.listBookingClassRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<BookingClassRuleItem>[] = [
    {
      headerKey: "policies.travel_booking_class.col.grade",
      label: "Grade",
      render: (r) => <span className="font-medium">{r.grade_code}</span>,
    },
    {
      headerKey: "policies.travel_booking_class.col.class",
      label: "Flight class",
      render: (r) => <span className="text-xs uppercase">{r.flight_class}</span>,
    },
    {
      headerKey: "policies.travel_booking_class.col.hotel",
      label: "Hotel cap",
      render: (r) => <span className="font-mono text-xs">{r.hotel_star_cap}★</span>,
    },
    {
      headerKey: "policies.travel_booking_class.col.transport",
      label: "Ground",
      render: (r) => <span className="text-xs text-text-secondary">{r.ground_transport_allowed.join(" · ")}</span>,
    },
    {
      headerKey: "policies.travel_booking_class.col.limit",
      label: "Daily limit (SAR)",
      render: (r) => <span className="font-mono text-xs">{r.daily_limit_sar.toFixed(0)}</span>,
    },
  ];

  return (
    <PolicyListPage<BookingClassRuleItem>
      title={t("policies.travel_booking_class.title", "Booking Class Rules")}
      subtitle={t("policies.travel_booking_class.subtitle", "Economy / Business / First per grade · hotel star caps · ground transport rules.")}
      agent="tashghil"
      domain="travel_booking_class"
      newButtonLabel={t("policies.travel_booking_class.cta.new", "New Booking Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.travel_booking_class.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.travel_booking_class.suggestion.clean_body", "Booking rules cap flight class + hotel star + ground transport per grade. Violations flagged on /operations/travel/active-travelers."),
          primaryActionLabel: t("policies.travel_booking_class.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "travel-booking-class-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.travel_booking_class.title", "Booking Class Rules"),
        window_label: t("policies.travel_booking_class.window", "across grades"),
      }}
    />
  );
}
